/* istanbul ignore file */

import { ServerListRequest } from '@lumapps/base-api';

/** Types of resources accepting v2 reactions */
export const REACTION_RESOURCE_TYPE = {
    article: 'article',
    event: 'event',
    comment: 'comment',
    content: 'content',
    post: 'post',
    'play-video': 'play-video',
} as const;

export type ReactionResourceType = keyof typeof REACTION_RESOURCE_TYPE;

export type ResourceRef<R extends string> = {
    /** Resource identifier */
    resourceId: string;
    /** Resource type */
    resourceType: R;
};

/** Reaction types (more to come) */
export enum REACTIONS {
    THUMBS_UP = '👍',
    HEART = '❤️',
    HUGGING_FACE = '🤗',
    CRY = '😢',
    JOY = '😂',
    FACE_WITH_OPEN_MOUTH = '😮',
}

export type ReactionType = `${REACTIONS}`;
export const REACTION_TYPES = Object.values(REACTIONS) as ReadonlyArray<ReactionType>;

/** Reaction resource */
export type ReactionResourceRef = ResourceRef<ReactionResourceType>;

export type ReactionCountsDetails = { count: number; type: ReactionType };
export type ReactionCounts = { total: number; details: ReactionCountsDetails[] };

/** Resource reaction */
export type ResourceReactionParams = ReactionResourceRef & { type: ReactionType };

/** Reaction state */
export type ReactionState = { currentUserReactions: ReactionType[]; reactionCounts: ReactionCounts };

/** Reactions for a user */
export interface UserReactions {
    user: { userId: string; fullName: string };
    reactions: ReactionType[];
}

/** Reaction details after add or delete */
export interface ReactionDetails {
    /** The total number of reactions for the specified reaction type. */
    count: number;
    /** Whether the current user has reacted with this reaction type. */
    hasUserReacted: boolean;
    /** The reaction type */
    type: ReactionType;
}

/** Metadata used on V2 resources to display comment count */
export interface WithComments {
    commentCount?: number;
}

/** Metadata used on V2 resources to display the current reactions */
export interface WithReactions extends ReactionState {}

/** Minimal user info (for reaction user list) */
export type MinimalUser = { id: string; fullName: string };

/** List params to fetch user reaction list */
export type ReactionListParams = Pick<ServerListRequest, 'cursor' | 'maxResults'> & {
    reactionType?: ReactionType | ReactionType[];
};
